class Slider {
  constructor(element, options = {}) {
    this.slider = element;
    this.container = element.querySelector('.slider__container');
    this.slides = element.querySelectorAll('.slide');

    this.options = {
      slideDuration: options.slideDuration || 0.5,
      easing: options.easing || 'power2.out',
      autoplay: options.autoplay || false,
      autoplaySpeed: options.autoplaySpeed || 3000,
      loop: options.loop || false,
      slideSpeed: options.slideSpeed || 500,
    };

    this.currentIndex = 0;
    this.slideWidth = 0;
    this.totalSlides = this.slides.length;
    this.autoplayInterval = null;
    this.startX = 0;
    this.isDragging = false;
    this.eventListeners = {};

    this.bindMethods();
    this.init();
  }

  bindMethods() {
    this.toPrev = this.toPrev.bind(this);
    this.toNext = this.toNext.bind(this);
    this.toIndex = this.toIndex.bind(this);
    this.startAutoplay = this.startAutoplay.bind(this);
    this.stopAutoplay = this.stopAutoplay.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
  }

  init() {
    this.calculateSlideWidth();
    this.toIndex(this.currentIndex);
    this.bindEvents();

    if (this.options.autoplay) {
      this.startAutoplay();
    }
  }

  calculateSlideWidth() {
    if (this.slides.length > 0) {
      const firstSlide = this.slides[0];
      const computedStyle = window.getComputedStyle(firstSlide);
      const marginRight = parseInt(computedStyle.marginRight, 10);
      this.slideWidth = firstSlide.offsetWidth + marginRight;
    }
  }

  toIndex(index) {
    if (this.options.loop) {
      index = (index + this.totalSlides) % this.totalSlides;
    } else {
      index = Math.max(0, Math.min(index, this.totalSlides - 1));
    }

    const prevIndex = this.currentIndex;
    this.currentIndex = index;

    this.slides.forEach((slide) => slide.classList.remove('is-active'));
    this.slides[this.currentIndex].classList.add('is-active');

    // Calculate center position
    const sliderWidth = this.slider.offsetWidth;
    const slideWidth = this.slideWidth;
    const slideGap = parseInt(
      window.getComputedStyle(this.slides[0]).marginRight,
      10,
    );
    const centerOffset = (sliderWidth - slideWidth) / 2;

    // Calculate target position including gaps between slides
    const targetPosition =
      -(slideWidth * this.currentIndex) + centerOffset + slideGap / 2;

    gsap.to(this.container, {
      x: targetPosition,
      duration: this.options.slideSpeed / 1000,
      ease: this.options.easing,
    });

    // Emit slideChange event
    this.emit('slideChange', {
      currentIndex: this.currentIndex,
      previousIndex: prevIndex,
      currentSlide: this.slides[this.currentIndex],
      previousSlide: this.slides[prevIndex],
    });
  }

  toPrev() {
    this.toIndex(this.currentIndex - 1);
    this.resetAutoplay();
  }

  toNext() {
    this.toIndex(this.currentIndex + 1);
    this.resetAutoplay();
  }

  startAutoplay() {
    if (!this.autoplayInterval) {
      this.autoplayInterval = setInterval(
        this.toNext,
        this.options.autoplaySpeed,
      );
    }
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  resetAutoplay() {
    if (this.options.autoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  on(eventName, callback) {
    if (!this.eventListeners[eventName]) {
      this.eventListeners[eventName] = [];
    }
    this.eventListeners[eventName].push(callback);
  }

  emit(eventName, data) {
    if (this.eventListeners[eventName]) {
      this.eventListeners[eventName].forEach((callback) => callback(data));
    }
  }

  bindEvents() {
    this.slides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        this.toIndex(index);
        this.resetAutoplay();
      });
    });

    this.slider.addEventListener('mouseenter', this.stopAutoplay);
    this.slider.addEventListener('mouseleave', () => {
      if (this.options.autoplay) {
        this.startAutoplay();
      }
    });

    window.addEventListener('resize', () => {
      this.calculateSlideWidth();
      this.toIndex(this.currentIndex);
    });

    document.addEventListener('keydown', this.handleKeydown);

    this.slider.addEventListener('touchstart', this.handleTouchStart);
    this.slider.addEventListener('touchmove', this.handleTouchMove);
    this.slider.addEventListener('touchend', this.handleTouchEnd);
  }

  handleKeydown(event) {
    if (event.key === 'ArrowLeft') {
      this.toPrev();
    } else if (event.key === 'ArrowRight') {
      this.toNext();
    }
  }

  handleTouchStart(event) {
    this.startX = event.touches[0].clientX;
    this.isDragging = true;
  }

  handleTouchMove(event) {
    if (!this.isDragging) return;
    const currentX = event.touches[0].clientX;
    const diffX = this.startX - currentX;

    if (Math.abs(diffX) > 50) {
      event.preventDefault();
      if (diffX > 0) {
        this.toNext();
      } else {
        this.toPrev();
      }
      this.isDragging = false;
    }
  }

  handleTouchEnd() {
    this.isDragging = false;
  }

  addSlide(slideElement) {
    this.container.appendChild(slideElement);
    this.slides = this.slider.querySelectorAll('.slide');
    this.totalSlides = this.slides.length;
    this.calculateSlideWidth();
  }
}

class VideoController {
  constructor(videoElement, muteBtn, pauseBtn) {
    this.video = videoElement;
    this.muteBtn = muteBtn;
    this.pauseBtn = pauseBtn;
    this.setupControls();
  }

  setupControls() {
    if (!this.video) return;

    // Initialize button states
    this.updateMuteButtonState();
    this.updatePauseButtonState();

    this.muteBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      // Instead of just toggling this video, emit a custom event
      const newMuteState = !this.video.muted;
      document.dispatchEvent(
        new CustomEvent('toggleAllVideosMute', {
          detail: { muted: newMuteState },
        }),
      );
    });

    this.pauseBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.togglePlayPause();
    });
  }

  setMuted(muted) {
    if (!this.video) return;
    this.video.muted = muted;
    this.updateMuteButtonState();
  }

  togglePlayPause() {
    if (!this.video) return;
    if (this.video.paused) {
      this.video
        .play()
        .then(() => {
          this.updatePauseButtonState();
        })
        .catch((err) => console.warn('Video playback failed:', err));
    } else {
      this.video.pause();
      this.updatePauseButtonState();
    }
  }

  updateMuteButtonState() {
    if (this.muteBtn) {
      this.muteBtn.textContent = this.video?.muted ? 'muted' : 'unmuted';
    }
  }

  updatePauseButtonState() {
    if (this.pauseBtn) {
      this.pauseBtn.textContent = this.video?.paused ? 'play' : 'pause';
    }
  }

  play() {
    this.video
      ?.play()
      .then(() => {
        this.updatePauseButtonState();
      })
      .catch((err) => console.warn('Video playback failed:', err));
  }

  pause() {
    this.video?.pause();
    this.updatePauseButtonState();
  }
}
